import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { format } from "date-fns";
import React from "react";
import { useGetMeterType } from "../../hooks";

export interface UsedMetersFilters {
  meter_type?: string;
  product_code?: string;
  ageRange?: string;
  uninstalledDateFrom?: string;
  uninstalledDateTo?: string;
  page?: number;
  limit?: number;
  search_data?: string;
  sort?: string;
  sort_direction?: "asc" | "desc";
}

interface UsedMetersFilterPanelProps {
  currentFilters: UsedMetersFilters;
  onApplyFilters: (filters: Partial<UsedMetersFilters>) => void;
  onResetFilters: () => void;
}

const utilityServiceOptions = [
  { value: "ALL_UTILITIES", label: "All Utility Services" },
  { value: "water", label: "Water" },
  { value: "gas", label: "Gas" },
  { value: "electricity", label: "Electricity" },
  { value: "waste_water", label: "Waste Water" },
  { value: "micro_grid", label: "Micro-grid" },
];

const ageRangeOptions = [
  { value: "ALL_AGES", label: "All Ages" },
  { value: "less_than_1", label: "Less than 1 year" },
  { value: "1_to_3", label: "1-3 years - Early operational phase" },
  { value: "3_to_5", label: "3-5 years - Mid-life operational meters" },
  { value: "5_to_10", label: "5-10 years - Mature operational devices" },
  { value: "10_to_15", label: "10-15 years - Late operational phase" },
  {
    value: "over_15",
    label: "Over 15 years - End-of-life consideration meters",
  },
];

export const UsedMetersFilterPanel: React.FC<UsedMetersFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const { data: meterTypeData } = useGetMeterType();
  console.log("meter typw", meterTypeData);
  // Define form fields using the dynamic form structure
  const formFields: FormField[] = [
    {
      name: "meter_type",
      label: "Meter Type",
      type: "select",
      placeholder: "Select meter type",
      options: meterTypeData ?? [],
      defaultValue: "ALL_METER_TYPES",
      containerClassName: "space-y-2",
      className: "w-full",
    },
    {
      name: "product_code",
      label: "Utility Service",
      type: "select",
      placeholder: "Select utility service",
      options: utilityServiceOptions,
      defaultValue: "ALL_UTILITIES",
      containerClassName: "space-y-2",
      className: "w-full",
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  // Handle form submission
  const handleApplyFilters = (formData: any) => {
    const filtersToApply: Partial<UsedMetersFilters> = {};

    // Only include non-default values
    if (formData.meter_type && formData.meter_type !== "ALL_METER_TYPES") {
      filtersToApply.meter_type = formData.meter_type;
    }
    if (formData.product_code && formData.product_code !== "ALL_UTILITIES") {
      filtersToApply.product_code = formData.product_code;
    }
    if (formData.ageRange && formData.ageRange !== "ALL_AGES") {
      filtersToApply.ageRange = formData.ageRange;
    }
    if (formData.uninstalledDateFrom) {
      const fromDate = new Date(formData.uninstalledDateFrom);
      filtersToApply.uninstalledDateFrom = format(fromDate, "yyyy-MM-dd");
    }
    if (formData.uninstalledDateTo) {
      const toDate = new Date(formData.uninstalledDateTo);
      filtersToApply.uninstalledDateTo = format(toDate, "yyyy-MM-dd");
    }

    onApplyFilters(filtersToApply);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    // Reset form to default values
    form.reset({
      meter_type: "ALL_METER_TYPES",
      product_code: "ALL_UTILITIES",
      ageRange: "ALL_AGES",
      uninstalledDateFrom: "",
      uninstalledDateTo: "",
    });

    // Call the parent reset handler
    onResetFilters();
  };

  // Custom footer with Apply and Reset buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-5 flex justify-end gap-2 pt-6 border-t border-gray-200">
      <Button
        type="button"
        variant="outline"
        onClick={handleResetFilters}
        className="px-4 py-2"
      >
        Reset Filters
      </Button>
      <Button type="submit" className="px-6 py-2" onClick={handleApplyFilters}>
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleApplyFilters}
        onReset={handleResetFilters}
        config={{
          layout: "horizontal",
          showSubmitButton: false,
          showResetButton: false,
          classes: {
            form: "w-full",
            fieldContainer: "flex flex-col space-y-1",
            label: "block text-sm font-medium text-gray-700 mb-1",
            input:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
            select:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white",
            error: "text-red-500 text-sm mt-1",
            helperText: "text-gray-500 text-sm mt-1",
          },
        }}
        gridColumns={5} // 5 columns for the 5 filter fields
        responsiveColumns={{
          sm: 1,
          md: 2,
          lg: 2,
        }}
        renderCustomFooter={renderCustomFooter}
        className="space-y-6"
      />
    </div>
  );
};

export default UsedMetersFilterPanel;
